// liveMouthDriver — a boca do LIVE por amplitude (LOTE 4.1, 05/09/2026).
//
// O AvatarCore (congelado) só move UM morph pelo caminho legado de amplitude
// (mouthOpen/jawOpen, ou o osso do maxilar) e sem assimetria. Este driver
// recebe o alvo ARKit52 pelo prop onLipSyncTargetReady (a única mudança
// autorizada nos congelados) e o analyser da voz remota, e distribui a
// amplitude por jawOpen + mouthFunnel + mouthPucker com ataque rápido e
// decaimento lento — fala não liga e desliga em degrau.
//
// REGRAS QUE O TESTE PINA (src/test/liveMouth.test.tsx):
//   · só escreve enquanto `speaking`; no listening a boca fica parada (senão o
//     avatar mexe a boca enquanto o USUÁRIO fala);
//   · ao sair de speaking decai até zero e PARA de escrever; no stop() zera
//     na hora — nunca deixa a boca aberta;
//   · NUNCA toca em morph dos outros três hooks do rosto (FACE_HOOK_MORPHS):
//     useEyeBlink, useMicroexpression e useFaceBaseline escrevem no MESMO
//     array de influences, e a corrida é invisível até alguém ver o rosto
//     tremer;
//   · morph ausente no dicionário é ignorado (o MetaPerson nativo não tem
//     os três).
//
// Puro por desenho: RAF, relógio e analyser entram por parâmetro para o
// teste dirigir frame a frame sem WebGL nem Web Audio.

import type { LipSyncMorphTarget } from "@/hooks/useLipSync";

export const LIVE_MOUTH_MORPHS = ["jawOpen", "mouthFunnel", "mouthPucker"] as const;
export type LiveMouthMorph = (typeof LIVE_MOUTH_MORPHS)[number];

/**
 * Peso de cada morph por unidade de amplitude (0..1). jawOpen respeita o teto
 * do useLipSync (0,4 — "0,55–0,6 lê como gritando" numa face fotorreal).
 */
export const LIVE_MOUTH_WEIGHTS: Record<LiveMouthMorph, number> = {
  jawOpen: 0.38,
  mouthFunnel: 0.22,
  mouthPucker: 0.12,
};
/** Ataque: a boca abre em ~2 frames. */
export const LIVE_MOUTH_ATTACK_TAU_MS = 35;
/** Decaimento: fecha em ~8 frames — o que faz a fala não parecer degrau. */
export const LIVE_MOUTH_RELEASE_TAU_MS = 140;
/** Mesma escala do caminho legado do AvatarCore e da sonda: min(média/80, 1). */
export const LIVE_MOUTH_FULL_SCALE = 80;
/** Abaixo disto, depois de sair de speaking, zera e para de escrever. */
export const LIVE_MOUTH_SILENCE = 0.015;

/**
 * O que useEyeBlink / useMicroexpression / useFaceBaseline escrevem (lido dos
 * três hooks em 05/09/2026). O driver NUNCA escreve aqui — pinado.
 */
export const FACE_HOOK_MORPHS = [
  "eyeBlinkLeft", "eyeBlinkRight",
  "browDownLeft", "browDownRight", "browInnerUp", "cheekPuff",
  "eyeSquintLeft", "eyeSquintRight", "noseSneerLeft", "noseSneerRight",
  "mouthSmileLeft", "mouthSmileRight",
] as const;

export type AnalyserLike = Pick<AnalyserNode, "frequencyBinCount" | "getByteFrequencyData">;

/** Índices dos três morphs no dicionário do mesh — só os que existem. */
export function liveMouthIndices(dict: LipSyncMorphTarget["morphTargetDictionary"]): Partial<Record<LiveMouthMorph, number>> {
  const out: Partial<Record<LiveMouthMorph, number>> = {};
  for (const m of LIVE_MOUTH_MORPHS) {
    const idx = dict[m];
    if (typeof idx === "number" && idx >= 0) out[m] = idx;
  }
  return out;
}

/**
 * O mesh tem pelo menos um dos três morphs da boca? É a MESMA pergunta de
 * `LiveMouthDriver.hasMouth`, respondida sem construir o driver — quem decide
 * se o driver assume a boca ou se o analyser segue ao legado do AvatarCore
 * precisa dela ANTES de render (review Codex P2 do LOTE 4.1: decidir só pela
 * presença do alvo deixava um mesh sem os três morphs sem driver E sem
 * analyser — boca parada sem ninguém para movê-la).
 */
export function hasLiveMouth(target: LipSyncMorphTarget | null | undefined): boolean {
  return !!target && Object.keys(liveMouthIndices(target.morphTargetDictionary)).length > 0;
}

/** Amplitude 0..1 do analyser: média das bins / 80, como o legado e a sonda. */
export function amplitudeFromAnalyser(an: AnalyserLike, buf: Uint8Array): number {
  an.getByteFrequencyData(buf);
  if (buf.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < buf.length; i++) sum += buf[i];
  return Math.min(1, sum / buf.length / LIVE_MOUTH_FULL_SCALE);
}

/** Suavização assimétrica: sobe com τ de ataque, desce com τ de decaimento. */
export function smoothAmplitude(prev: number, target: number, dtMs: number): number {
  const tau = target > prev ? LIVE_MOUTH_ATTACK_TAU_MS : LIVE_MOUTH_RELEASE_TAU_MS;
  const dt = Number.isFinite(dtMs) && dtMs > 0 ? dtMs : 16;
  const k = 1 - Math.exp(-dt / tau);
  return prev + (target - prev) * k;
}

/** Distribuição da amplitude pelos três morphs. */
export function mouthWeights(amplitude: number): Record<LiveMouthMorph, number> {
  const a = Math.min(1, Math.max(0, amplitude));
  return {
    jawOpen: a * LIVE_MOUTH_WEIGHTS.jawOpen,
    mouthFunnel: a * LIVE_MOUTH_WEIGHTS.mouthFunnel,
    mouthPucker: a * LIVE_MOUTH_WEIGHTS.mouthPucker,
  };
}

export interface LiveMouthDriverDeps {
  raf?: (cb: (t: number) => void) => number;
  caf?: (id: number) => void;
  now?: () => number;
}

export class LiveMouthDriver {
  private readonly indices: Partial<Record<LiveMouthMorph, number>>;
  private readonly buf: Uint8Array;
  private readonly raf: (cb: (t: number) => void) => number;
  private readonly caf: (id: number) => void;
  private readonly now: () => number;
  private speaking = false;
  private running = false;
  private scheduled: number | null = null;
  private amplitude = 0;
  private lastT: number | null = null;
  /** Quantas vezes escreveu nos influences — só para o teste provar "parado no listening". */
  writes = 0;

  constructor(
    private readonly target: LipSyncMorphTarget,
    private readonly analyser: AnalyserLike,
    deps: LiveMouthDriverDeps = {},
  ) {
    this.indices = liveMouthIndices(target.morphTargetDictionary);
    this.buf = new Uint8Array(Math.max(0, analyser.frequencyBinCount));
    this.raf = deps.raf ?? ((cb) => window.requestAnimationFrame(cb));
    this.caf = deps.caf ?? ((id) => window.cancelAnimationFrame(id));
    this.now = deps.now ?? (() => performance.now());
  }

  /** Há pelo menos um dos três morphs neste mesh? Sem nenhum, o driver é inerte (= hasLiveMouth). */
  get hasMouth(): boolean {
    return Object.keys(this.indices).length > 0;
  }

  setSpeaking(v: boolean): void {
    this.speaking = v;
    if (v && this.running && this.scheduled === null) this.schedule();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastT = null;
    if (this.speaking) this.schedule();
  }

  /** Zera os três na hora e para. Idempotente. */
  stop(): void {
    this.running = false;
    if (this.scheduled !== null) {
      this.caf(this.scheduled);
      this.scheduled = null;
    }
    this.amplitude = 0;
    this.write(0);
  }

  private schedule(): void {
    this.scheduled = this.raf((t) => {
      this.scheduled = null;
      this.frame(t);
    });
  }

  /** Um frame. Público para o teste dirigir sem RAF. */
  frame(t: number = this.now()): void {
    if (!this.running) return;
    const dt = this.lastT === null ? 16 : t - this.lastT;
    this.lastT = t;
    const targetAmp = this.speaking ? amplitudeFromAnalyser(this.analyser, this.buf) : 0;
    this.amplitude = smoothAmplitude(this.amplitude, targetAmp, dt);
    if (!this.speaking && this.amplitude < LIVE_MOUTH_SILENCE) {
      // Saiu de speaking e já decaiu: zera de vez e PARA de escrever até o
      // próximo speaking. No listening a boca fica parada.
      this.amplitude = 0;
      this.write(0);
      this.lastT = null;
      return;
    }
    this.write(this.amplitude);
    this.schedule();
  }

  private write(amplitude: number): void {
    const w = mouthWeights(amplitude);
    const infl = this.target.morphTargetInfluences;
    let wrote = false;
    for (const m of LIVE_MOUTH_MORPHS) {
      const idx = this.indices[m];
      if (idx === undefined) continue;
      infl[idx] = w[m];
      wrote = true;
    }
    if (wrote) this.writes += 1;
  }
}
